import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "@/components/brand";
import { useLang, NAV_LABELS } from "@/lib/language";

export function LanguageToggle({ lang, setLang, compact = false }) {
  return (
    <button
      data-testid="language-selector"
      onClick={() => setLang(lang === "en" ? "hi" : "en")}
      className={`inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white font-medium text-slate-700 transition-colors hover:border-orange-300 hover:text-orange-700 ${
        compact ? "px-3 py-1.5 text-xs" : "px-3.5 py-2 text-sm"
      }`}
      aria-label="Switch language between English and Hindi"
    >
      <span className={lang === "en" ? "text-orange-600" : ""}>EN</span>
      <span className="text-slate-300">/</span>
      <span className={lang === "hi" ? "text-orange-600" : ""}>हिंदी</span>
      <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
    </button>
  );
}

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState({ authenticated: false, user: null });
  const { lang, setLang } = useLang();
  const t = NAV_LABELS[lang];
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/wp-json/csai/v1/session", { credentials: "same-origin" })
      .then((response) => response.json())
      .then((data) => setSession({
        authenticated: Boolean(data.authenticated),
        user: data.user || null,
      }))
      .catch(() => setSession({ authenticated: false, user: null }));
  }, []);

  const logout = async () => {
    try {
      await fetch("/wp-json/csai/v1/logout", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
    } finally {
      localStorage.removeItem("csai_user");
      window.location.href = "/login";
    }
  };

  const accountPath = session.user?.onboarding_complete ? "/dashboard" : "/onboarding";
  const accountLabel = session.user?.onboarding_complete ? "Dashboard" : "Continue Setup";

  const links = [
    { label: t.product, href: "/#features", testid: "nav-product-link" },
    { label: t.how, href: "/#how-it-works", testid: "nav-how-it-works-link" },
    { label: t.pricing, href: "/pricing", testid: "nav-pricing-link" },
    { label: t.contact, href: "/#contact", testid: "nav-contact-link" },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-slate-100 bg-white/85 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex" data-testid="site-nav">
          {links.map((l) => (
            <button
              key={l.testid}
              data-testid={l.testid}
              onClick={() => {
                if (l.href.startsWith("/#")) {
                  navigate("/");
                  setTimeout(() => document.querySelector(l.href.slice(1))?.scrollIntoView({ behavior: "smooth" }), 100);
                } else navigate(l.href);
              }}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-orange-50 hover:text-slate-900"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <LanguageToggle lang={lang} setLang={setLang} />
          {session.authenticated ? (
            <>
              <button
                type="button"
                data-testid="nav-logout-button"
                onClick={logout}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-orange-50 hover:text-slate-900"
              >
                Logout
              </button>
              <Link
                to={accountPath}
                data-testid="nav-account-button"
                className="rounded-xl bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-orange-700 hover:shadow-md"
              >
                {accountLabel}
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-orange-50 hover:text-slate-900"
              >
                {t.login}
              </Link>
              <Link
                to="/login?mode=signup"
                data-testid="nav-start-setup-button"
                className="rounded-xl bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-orange-700 hover:shadow-md"
              >
                {t.start}
              </Link>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 lg:hidden">
          <LanguageToggle lang={lang} setLang={setLang} compact />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="rounded-lg p-2 text-slate-700 hover:bg-orange-50"
            aria-label="Open menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-100 bg-white px-4 py-4 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.testid}
                data-testid={`mobile-${l.testid}`}
                onClick={() => {
                  setOpen(false);
                  if (l.href.startsWith("/#")) {
                    navigate("/");
                    setTimeout(() => document.querySelector(l.href.slice(1))?.scrollIntoView({ behavior: "smooth" }), 120);
                  } else navigate(l.href);
                }}
                className="rounded-lg px-4 py-3 text-left text-base font-medium text-slate-700 hover:bg-orange-50"
              >
                {l.label}
              </button>
            ))}
            {session.authenticated ? (
              <>
                <button
                  type="button"
                  data-testid="mobile-nav-logout-button"
                  onClick={logout}
                  className="rounded-lg px-4 py-3 text-left text-base font-medium text-slate-700 hover:bg-orange-50"
                >
                  Logout
                </button>
                <Link
                  to={accountPath}
                  data-testid="mobile-nav-account-button"
                  onClick={() => setOpen(false)}
                  className="mt-2 rounded-xl bg-orange-600 px-5 py-3.5 text-center text-base font-semibold text-white"
                >
                  {accountLabel}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="mobile-nav-login-link"
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-4 py-3 text-base font-medium text-slate-700 hover:bg-orange-50"
                >
                  {t.login}
                </Link>
                <Link
                  to="/login?mode=signup"
                  data-testid="mobile-nav-start-setup-button"
                  onClick={() => setOpen(false)}
                  className="mt-2 rounded-xl bg-orange-600 px-5 py-3.5 text-center text-base font-semibold text-white"
                >
                  {t.start}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
